/**
 * HR Settings Tab Component
 * Configure evaluation reminder settings
 */
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Save } from "lucide-react";

export function HRSettingsTab({
  settings,
  onChange,
  onSave,
  saving
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="hr-settings-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Evaluation Reminder Settings</h2>
          <p className="text-sm text-slate-500">Configure automatic reminders for PCW evaluations awaiting acknowledgment</p>
        </div>
        <Button onClick={onSave} disabled={saving} className="gap-2">
          <Save className="w-4 h-4" />
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>

      {/* Enable Toggle */}
      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg mb-6">
        <div>
          <p className="font-medium text-slate-900">Enable Automatic Reminders</p>
          <p className="text-sm text-slate-500">Send automatic email reminders for unviewed evaluations</p>
        </div>
        <Switch
          checked={settings.enabled || false}
          onCheckedChange={(checked) => onChange({ ...settings, enabled: checked })}
          data-testid="eval-reminder-toggle"
        />
      </div>

      {settings.enabled && (
        <div className="space-y-6">
          {/* Timing Settings */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>First Reminder After (days)</Label>
              <Input
                type="number"
                min="1"
                max="30"
                value={settings.first_reminder_days || 3}
                onChange={(e) => onChange({ 
                  ...settings, 
                  first_reminder_days: parseInt(e.target.value) || 3 
                })}
                data-testid="first-reminder-days"
              />
              <p className="text-xs text-slate-500">Days after sending before first reminder</p>
            </div>
            <div className="space-y-2">
              <Label>Subsequent Reminders Every (days)</Label>
              <Input
                type="number"
                min="1"
                max="30"
                value={settings.subsequent_reminder_days || 7}
                onChange={(e) => onChange({ 
                  ...settings, 
                  subsequent_reminder_days: parseInt(e.target.value) || 7 
                })}
                data-testid="subsequent-reminder-days"
              />
              <p className="text-xs text-slate-500">Days between follow-up reminders</p>
            </div>
            <div className="space-y-2">
              <Label>Maximum Reminders</Label>
              <Input
                type="number"
                min="1"
                max="10"
                value={settings.max_reminders || 3}
                onChange={(e) => onChange({ 
                  ...settings, 
                  max_reminders: parseInt(e.target.value) || 3 
                })}
                data-testid="max-reminders"
              />
              <p className="text-xs text-slate-500">Stop after this many reminders</p>
            </div>
          </div>

          {/* Email Settings */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Reminder Email Subject</Label>
              <Input
                value={settings.reminder_email_subject || ""}
                onChange={(e) => onChange({ 
                  ...settings, 
                  reminder_email_subject: e.target.value 
                })}
                placeholder="Reminder: Please Review Your Performance Evaluation"
                data-testid="reminder-email-subject"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-700">Include Sign Link in Reminder</p>
                <p className="text-sm text-slate-500">Add the direct acknowledgment link to reminder emails</p>
              </div>
              <Switch
                checked={settings.include_link_in_reminder || false}
                onCheckedChange={(checked) => onChange({ 
                  ...settings, 
                  include_link_in_reminder: checked 
                })}
                data-testid="include-link-toggle"
              />
            </div>
          </div>

          {/* Example Timeline */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-800 mb-2">Example Reminder Timeline</h4>
            <p className="text-sm text-blue-700">
              With current settings, if an evaluation is sent and not viewed:
            </p>
            <ul className="text-sm text-blue-700 mt-2 space-y-1">
              <li>• 1st reminder: Day {settings.first_reminder_days || 3}</li>
              <li>• 2nd reminder: Day {(settings.first_reminder_days || 3) + (settings.subsequent_reminder_days || 7)}</li>
              {(settings.max_reminders || 3) >= 3 && (
                <li>• 3rd reminder: Day {(settings.first_reminder_days || 3) + ((settings.subsequent_reminder_days || 7) * 2)}</li>
              )}
            </ul>
          </div>
        </div>
      )}

      {!settings.enabled && (
        <p className="text-sm text-slate-500 text-center py-4">
          Automatic reminders are disabled. Toggle the switch above to configure reminder settings.
        </p>
      )}
    </div>
  );
}
